import React, { useState, useEffect, useCallback, useRef, createRef, useMemo } from 'react';
import {
   Modal,
   ModalBody,
   ModalOverlay,
   ModalHeader,
   ModalContent,
   ModalCloseButton,
   ModalFooter,
   Button,
   useColorModeValue,
   Box,
   Grid,
   GridItem,
   Flex,
} from '@chakra-ui/react';
import InputField from 'components/ui/InputField';

const TakaForm = ({ isOpen, onClose, handleTakaDetails, currentTakaDetails, onCloseComplete }) => {
   const [rows, setRows] = useState([]);
   const qtyRef = useRef([]);
   const submitRef = useRef(null);

   const takaDetails = useMemo(() => {
      return currentTakaDetails.map((item) => ({ qty: item.quantity3 }));
   }, [currentTakaDetails]);

   const totalTaka = useMemo(() => {
      return rows.reduce((acc, item) => acc + Number(item?.qty || 0), 0);
   }, [rows]);

   const prepareRowRefs = (count) => {
      qtyRef.current = Array(count)
         ?.fill()
         ?.map((_, i) => qtyRef.current[i] || createRef());
   };

   const handleAddRow = useCallback(() => {
      setRows([...rows, { qty: '' }]);
   }, [rows.length]);

   const handleRemoveRow = useCallback(
      (index) => {
         setRows(rows.filter((_, i) => i !== index));
      },
      [rows],
   );

   const handleOnClose = useCallback(() => {
      setRows([]);
      onClose();
   }, [onClose]);

   const handleKeyDown = useCallback(
      (e, nextRef, idx) => {
         const key = e.key;
         const isShift = e.shiftKey;
         const name = e.target.name;
         const value = e.target.value.trim();

         // Shift + Tab: Move backward
         if (key === 'Tab' && isShift) {
            e.preventDefault(); // Prevent default tabbing
            nextRef?.current[idx - 1]?.focus();
            return;
         }

         // Enter or Tab (no shift): Move forward or add row
         if (key === 'Enter' || (key === 'Tab' && !isShift)) {
            e.preventDefault(); // Prevent default behavior

            if (value !== '' && name === 'qty') {
               handleAddRow();
               nextRef?.current[idx + 1]?.focus();
            } else {
               handleRemoveRow(idx);
               submitRef.current?.focus(); // Go to submit button or similar
            }
         }
      },
      [handleAddRow, handleRemoveRow],
   );

   const handleOnChange = (e, idx) => {
      const updatedRows = [...rows];
      updatedRows[idx].qty = e.target.value;
      setRows(updatedRows);
   };

   const handleOnSubmit = () => {
      const takaDetails = rows;
      handleTakaDetails(takaDetails);
      handleOnClose();
   };

   useEffect(() => {
      prepareRowRefs(rows?.length);
   }, [rows?.length]);

   useEffect(() => {
      if (rows?.length === 0) {
         setRows([{ qty: '' }]);
         if (qtyRef && qtyRef?.current[1]) {
            qtyRef?.current[1]?.focus();
         }
      }
   }, [rows]);

   useEffect(() => {
      if (takaDetails?.length > 0) {
         setRows(takaDetails);
      }
   }, [takaDetails]);

   return (
      <Modal isOpen={isOpen} onClose={handleOnClose} isCentered onCloseComplete={onCloseComplete}>
         0
         <ModalOverlay />
         <ModalContent
            bg={useColorModeValue('white', '#2d3748')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="20px"
            p={5}
            boxShadow="lg">
            <ModalHeader>
               <Flex justifyContent="space-between" alignItems="center">
                  <Box>Add Taka Details ({rows?.length})</Box>
                  <Box>Total: {totalTaka}</Box>
               </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody fontSize="20px" fontWeight="600" mb={4}>
               <Box width="100%" maxH="400px" overflowY="auto">
                  {rows.map((row, idx) => {
                     return (
                        <Box mb={2} key={idx}>
                           <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                              <GridItem>
                                 <InputField
                                    ref={(el) => {
                                       if (qtyRef && qtyRef.current) qtyRef.current[idx] = el;
                                    }}
                                    type="number"
                                    id={`qty-${idx}`}
                                    name="qty"
                                    label="MTS"
                                    value={row.qty}
                                    onChange={(e) => handleOnChange(e, idx)}
                                    onKeyDown={(e) => handleKeyDown(e, qtyRef, idx)}
                                    autoFocus={idx === rows?.length - 1}
                                 />
                              </GridItem>
                              <GridItem>
                                 <Flex gap={2} justifyContent="flex-start" alignItems="center" mt={1}>
                                    {rows?.length > 1 && (
                                       <Button
                                          colorScheme="red"
                                          size="sm"
                                          onClick={() => handleRemoveRow(idx)}
                                          _focus={{ outlineColor: 'blue.500' }}>
                                          -
                                       </Button>
                                    )}
                                    {idx === rows?.length - 1 && (
                                       <Button
                                          // ref={plusRef}
                                          colorScheme="green"
                                          size="sm"
                                          onClick={handleAddRow}
                                          _focus={{ outlineColor: 'blue.500' }}>
                                          +
                                       </Button>
                                    )}
                                 </Flex>
                              </GridItem>
                           </Grid>
                        </Box>
                     );
                  })}
               </Box>
            </ModalBody>
            <ModalFooter>
               <Flex>
                  <Button
                     ref={submitRef}
                     colorScheme="blue"
                     ml={3}
                     width="100%"
                     onClick={() => handleOnSubmit()}
                     _focus={{ outlineColor: 'blue.500' }}
                     // isDisabled={isLoading}
                     // isLoading={isLoading}
                  >
                     Save
                  </Button>
                  <Button colorScheme="red" onClick={() => handleOnClose()} ml={3} width="100%">
                     Cancel
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default TakaForm;
